import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, DatePicker, Input, InputNumber, Modal, Spin, message } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { taskActualApis } from "../../../../api/TaskSchedule";
import { fmtINR, num } from "./executionShared";

// Editable month grid for the CLIENT-approved monthly program (project ×
// month × ₹). Loads the saved plan, lets the user add/edit/remove month rows,
// and saves as a full replace-set. Deliberately month-level money — no task
// grain (see the report's tier model).

interface PlanRow {
  month: string; // 'YYYY-MM'
  amount: number | null;
  remarks: string;
}

const ClientPlanModal = ({
  open,
  onClose,
  projectId,
  onSaved,
  derivedByMonth,
}: {
  open: boolean;
  onClose: () => void;
  projectId: number | string;
  onSaved?: () => void;
  /** 'YYYY-MM' -> the schedules-derived amount, so the user can see what they override. */
  derivedByMonth?: Record<string, number>;
}) => {
  const [rows, setRows] = useState<PlanRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [addMonth, setAddMonth] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    setError("");
    setLoading(true);
    taskActualApis
      .getClientPlan(projectId)
      .then((res) => {
        if (cancelled) return;
        if (res?.status === true) {
          setRows(
            (res?.data?.months || []).map((m: any) => ({
              month: dayjs(m.planMonth).format("YYYY-MM"),
              amount: Number(m.plannedAmount) || 0,
              remarks: m.remarks || "",
            }))
          );
        } else {
          setError(res?.message || "Failed to load the planned revenue");
        }
      })
      .catch(() => !cancelled && setError("Failed to load the planned revenue"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  const addRow = useCallback(() => {
    if (!addMonth) return;
    const month = addMonth.format("YYYY-MM");
    setRows((prev) => {
      if (prev.some((row) => row.month === month)) {
        message.warning(`${addMonth.format("MMM YYYY")} is already in the plan`);
        return prev;
      }
      return [...prev, { month, amount: null, remarks: "" }].sort((a, b) =>
        a.month.localeCompare(b.month)
      );
    });
    setAddMonth(null);
  }, [addMonth]);

  const total = useMemo(
    () => rows.reduce((acc, row) => acc + (Number(row.amount) || 0), 0),
    [rows]
  );

  const doSave = useCallback(async () => {
    try {
      setSaving(true);
      setError("");
      const res = await taskActualApis.saveClientPlan(
        Number(projectId),
        rows
          .filter((row) => row.amount !== null)
          .map((row) => ({
            planMonth: `${row.month}-01`,
            plannedAmount: String(Number(row.amount) || 0),
            ...(row.remarks.trim() ? { remarks: row.remarks.trim() } : {}),
          }))
      );
      if (res?.status === true) {
        message.success(res?.data?.message || "Planned revenue saved");
        onSaved?.();
        onClose();
      } else {
        setError(res?.message || "Failed to save the planned revenue");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to save the planned revenue");
    } finally {
      setSaving(false);
    }
  }, [projectId, rows, onSaved, onClose]);

  const onSaveClick = useCallback(() => {
    if (!rows.filter((row) => row.amount !== null).length) {
      Modal.confirm({
        title: "Clear the planned revenue?",
        content:
          "No month amounts are set — saving clears all overrides, and every month falls back to the derived billing program.",
        okText: "Clear Plan",
        okButtonProps: { danger: true },
        onOk: doSave,
      });
      return;
    }
    doSave();
  }, [rows, doSave]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={680}
      title={<span style={{ color: "#022c57" }}>Planned Revenue (Client) — Monthly Program (₹)</span>}
      destroyOnClose
    >
      <p style={{ color: "#8c8c8c", fontSize: 12.5, marginBottom: 12 }}>
        By default the Planned Revenue (Client) row is DERIVED from the schedules
        (scheduled qty × Basic Rate). Enter amounts here only for months where the
        client's approved program differs — an entered month overrides the
        derived value; months left out keep it.
      </p>
      {/* The basis has to be stated at the point of entry. Planned and Executed
          Expenditure are both net, so a gross figure typed here would sit ~16% above
          them in the same column and land in the same TOTAL. */}
      <p
        style={{
          color: "#022c57",
          background: "#f4f8fd",
          border: "1px solid #e4e9f0",
          borderRadius: 6,
          padding: "8px 10px",
          fontSize: 12.5,
          marginBottom: 12,
        }}
      >
        Enter amounts <strong>excluding GST and labour cess</strong> — the Basic Rate
        basis, matching Planned and Executed Expenditure. The derived figure shown against
        each month is on that basis, so use it as your reference.
      </p>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <DatePicker
          picker="month"
          format="MMM YYYY"
          value={addMonth}
          onChange={(value) => setAddMonth(value)}
          placeholder="Add month"
        />
        <Button icon={<PlusOutlined />} onClick={addRow} disabled={!addMonth}>
          Add
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          <Spin />
        </div>
      ) : !rows.length ? (
        <div
          style={{
            border: "1px dashed #dbdfea",
            borderRadius: 6,
            padding: "18px 12px",
            textAlign: "center",
            color: "#8094ae",
            marginBottom: 12,
          }}
        >
          No planned revenue yet — add the months the client program covers.
        </div>
      ) : (
        <div
          style={{
            border: "1px solid #dbdfea",
            borderRadius: 6,
            marginBottom: 12,
            maxHeight: 340,
            overflowY: "auto",
          }}
        >
          {rows.map((row, idx) => (
            <div
              key={row.month}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "6px 12px",
                borderTop: idx === 0 ? "none" : "1px solid #f0f2f7",
              }}
            >
              <span style={{ width: 110, color: "#022c57", fontWeight: 500 }}>
                {dayjs(`${row.month}-01`).format("MMM YYYY")}
              </span>
              <InputNumber
                min={0}
                style={{ width: 180 }}
                placeholder="Amount excl. GST & cess"
                value={row.amount as any}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{2})+(\d{3})(?!\d))/g, ",")
                }
                onChange={(value) =>
                  setRows((prev) =>
                    prev.map((r) =>
                      r.month === row.month
                        ? { ...r, amount: value === null ? null : Number(value) }
                        : r
                    )
                  )
                }
              />
              <Input
                style={{ flex: 1 }}
                placeholder="remark (optional)"
                value={row.remarks}
                maxLength={500}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((r) =>
                      r.month === row.month ? { ...r, remarks: e.target.value } : r
                    )
                  )
                }
              />
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() =>
                  setRows((prev) => prev.filter((r) => r.month !== row.month))
                }
              />
              {/* What the report would show if this month were left out — the anchor
                  that stops someone typing a gross figure by mistake. */}
              {derivedByMonth?.[row.month] !== undefined && (
                <span
                  style={{
                    fontSize: 11,
                    color: "#8094ae",
                    whiteSpace: "nowrap",
                    fontVariantNumeric: "tabular-nums",
                  }}
                  title="The schedules-derived amount for this month (net of GST and labour cess). Leave the month out to use it."
                >
                  derived {fmtINR(derivedByMonth[row.month])}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          background: "#f5f6fa",
          borderRadius: 6,
          marginBottom: 12,
        }}
      >
        <span style={{ color: "#526484" }}>
          Total plan: <b style={{ color: "#022c57" }}>{fmtINR(num(total))}</b>
        </span>
        <span style={{ color: "#8094ae", fontSize: 12 }}>
          {rows.length} month{rows.length === 1 ? "" : "s"}
        </span>
      </div>

      {error && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }} message={error} />
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="primary" loading={saving} onClick={onSaveClick}>
          Save Planned Revenue
        </Button>
      </div>
    </Modal>
  );
};

export default ClientPlanModal;
